/**
 * NoteCard — one Onthouden item with accessible status controls (spec §8.2).
 *
 * Every control is a labelled button (visible text + icon, or IconButton with
 * aria-label); toggles expose aria-pressed. Status is never color-only:
 * StatusChip/SyncStatus always pair icon with text.
 */
import { Button, Card, IconButton, StatusChip, SyncStatus } from '@/components/ui'
import { AlertIcon, CheckIcon, EditIcon, PinIcon, TrashIcon } from '@/components/icons'
import type { Note, SyncSaveState } from '@/types'

function formatUpdatedAt(iso: string): string {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ''
  return new Intl.DateTimeFormat('nl-NL', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date)
}

export type NoteCardProps = {
  note: Note
  syncState: SyncSaveState | null
  onEdit: (note: Note) => void
  onTogglePin: (note: Note) => void
  onToggleUrgent: (note: Note) => void
  onToggleDone: (note: Note) => void
  onToggleArchive: (note: Note) => void
  onDelete: (note: Note) => void
}

export function NoteCard({
  note,
  syncState,
  onEdit,
  onTogglePin,
  onToggleUrgent,
  onToggleDone,
  onToggleArchive,
  onDelete,
}: NoteCardProps) {
  const busy = syncState === 'syncing'
  const updatedAt = formatUpdatedAt(note.updatedAt)

  return (
    <Card padding="sm" className="reminders-note-card">
      <div className="reminders-note-head">
        <div className="reminders-note-heading">
          <h3 className="reminders-note-title">{note.title}</h3>
          <div className="reminders-note-chips">
            {note.pinned ? <StatusChip status="info">Vastgezet</StatusChip> : null}
            {note.urgent ? <StatusChip status="warning">Urgent</StatusChip> : null}
            {note.done ? <StatusChip status="success">Gedaan</StatusChip> : null}
            {note.archived ? <StatusChip status="neutral">Gearchiveerd</StatusChip> : null}
          </div>
        </div>
        <IconButton aria-label={`Bewerk “${note.title}”`} onClick={() => onEdit(note)} disabled={busy}>
          <EditIcon />
        </IconButton>
      </div>

      {note.content ? <p className="reminders-note-content">{note.content}</p> : null}

      <p className="reminders-note-meta">
        {note.category}
        {updatedAt ? ` · bijgewerkt ${updatedAt}` : ''}
      </p>

      <SyncStatus state={syncState} />

      <div className="reminders-note-actions" role="group" aria-label={`Acties voor “${note.title}”`}>
        <button
          type="button"
          className="reminders-toggle"
          aria-pressed={note.pinned}
          onClick={() => onTogglePin(note)}
          disabled={busy || note.archived}
        >
          <PinIcon size={18} />
          {note.pinned ? 'Losmaken' : 'Vastzetten'}
        </button>
        <button
          type="button"
          className="reminders-toggle"
          aria-pressed={note.urgent}
          onClick={() => onToggleUrgent(note)}
          disabled={busy || note.archived}
        >
          <AlertIcon size={18} />
          Urgent
        </button>
        <button
          type="button"
          className="reminders-toggle"
          aria-pressed={note.done}
          onClick={() => onToggleDone(note)}
          disabled={busy || note.archived}
        >
          <CheckIcon size={18} />
          {note.done ? 'Heropenen' : 'Gedaan'}
        </button>
        <Button variant="ghost" size="sm" onClick={() => onToggleArchive(note)} disabled={busy}>
          {note.archived ? 'Terugzetten' : 'Archiveren'}
        </Button>
        <IconButton
          aria-label={`Verwijder “${note.title}”`}
          variant="danger"
          onClick={() => onDelete(note)}
          disabled={busy}
        >
          <TrashIcon />
        </IconButton>
      </div>
    </Card>
  )
}
